"use client"

import { useState, useRef, useEffect } from "react"
import { Button } from "./ui/button"
import { Menu, X, Shield, ChevronDown } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { Link } from "react-router-dom"

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [scrolled, setScrolled] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled)
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [scrolled])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const toggleDropdown = (name: string) => {
    setActiveDropdown(activeDropdown === name ? null : name)
  }

  // Animation variants
  const dropdownVariants = {
    hidden: { opacity: 0, y: -10, height: 0 },
    visible: {
      opacity: 1,
      y: 0,
      height: "auto",
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      y: -10,
      height: 0,
      transition: {
        duration: 0.2,
        ease: "easeIn",
      },
    },
  }

  return (
      <header className={`fixed top-0 left-0 right-0 z-50 ${
          scrolled ? "backdrop-blur-xl bg-black/50" : "bg-transparent"
      }`}
      >
        <div
            className={`flex items-center justify-between px-6 py-4 transition-all duration-300 `}
        >
          <div className="flex items-center gap-2">
            <a href="/" className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-red-600" />
              <span className="font-bold text-white text-xl">S&M</span>
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8 ">
            <div className="relative" ref={dropdownRef}>
              <a
                  className="flex flex-row items-center justify-center gap-1 text-sm text-gray-300 hover:text-white"
                  onClick={() => toggleDropdown("partners")}
                  onMouseEnter={() => setActiveDropdown("partners")}
              >
                Partenaires
                <ChevronDown
                    className={`w-4 h-4 transition-transform ${activeDropdown === "partners" ? "rotate-180" : ""}`}
                />
              </a>

              <AnimatePresence>
                {activeDropdown === "partners" && (
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={dropdownVariants}
                        className="bg-black/60 backdrop-blur-xl absolute top-full left-0 mt-2 w-56 border border-white/10 rounded-md shadow-lg py-2 overflow-hidden z-50"
                        onMouseLeave={() => setActiveDropdown(null)}
                    >
                      <a
                          href="/partners"
                          className="block px-4 py-2 text-sm text-gray-300 hover:bg-red-600/10 hover:text-white"
                      >
                        Nos Partenaires
                      </a>
                      <a
                          href="/partners/info"
                          className="block px-4 py-2 text-sm text-gray-300 hover:bg-red-600/10 hover:text-white"
                      >
                        Comment devenir partenaire
                      </a>
                      <a
                          href="/partners/apply"
                          className="block px-4 py-2 text-sm text-gray-300 hover:bg-red-600/10 hover:text-white"
                      >
                        Candidature partenaire
                      </a>
                    </motion.div>
                )}
              </AnimatePresence>
            </div>

            <a href="/solutions" className="text-sm text-gray-300 hover:text-white transition-colors">
              Solutions
            </a>
            <a href="/services" className="text-sm text-gray-300 hover:text-white transition-colors">
              Services
            </a>
            <a href="/how-it-works" className="text-sm text-gray-300 hover:text-white transition-colors">
              Comment ça marche
            </a>
            <a href="/about" className="text-sm text-gray-300 hover:text-white transition-colors">
              À propos
            </a>
          </nav>

          <div className="flex items-center gap-4">
            <Link to="/contact">
              <Button variant="primary" className="hidden md:flex">
                Commencer
              </Button>
            </Link>

            {/* Mobile Menu Button */}
            <button className="border-none outline-none cursor-pointer hover:text-red-500 md:hidden text-white duration-100" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
              <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`md:hidden overflow-hidden ${
                      scrolled ? "bg-transparent" : "backdrop-blur-xl bg-black/50"
                  }`}
              >
                <nav className="flex flex-col py-4 px-6">
                  {/* Partners Dropdown for Mobile */}
                  <div className="py-3">
                    <button
                        className="flex items-center gap-1 text-gray-300 hover:text-white transition-colors w-full text-left"
                        onClick={() => toggleDropdown("partners-mobile")}
                    >
                      Partners
                      <ChevronDown
                          className={`w-4 h-4 ml-1 transition-transform ${activeDropdown === "partners-mobile" ? "rotate-180" : ""}`}
                      />
                    </button>

                    <AnimatePresence>
                      {activeDropdown === "partners-mobile" && (
                          <motion.div
                              initial="hidden"
                              animate="visible"
                              exit="exit"
                              variants={dropdownVariants}
                              className="pl-4 mt-2 border-l border-white/10 overflow-hidden"
                          >
                            <a href="/partners" className="block py-2 text-sm text-gray-300 hover:text-white">
                              Our Partners
                            </a>
                            <a href="/partners/info" className="block py-2 text-sm text-gray-300 hover:text-white">
                              How to become partner
                            </a>
                            <a href="/partners/apply" className="block py-2 text-sm text-gray-300 hover:text-white">
                              Become a partner
                            </a>
                          </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <a href="/solutions" className="py-3 text-gray-300 hover:text-white transition-colors">
                    Solutions
                  </a>
                  <a href="/services" className="py-3 text-gray-300 hover:text-white transition-colors">
                    Services
                  </a>
                  <a href="/how-it-works" className="py-3 text-gray-300 hover:text-white transition-colors">
                    How It Works
                  </a>
                  <a href="/about" className="py-3 text-gray-300 hover:text-white transition-colors">
                    About
                  </a>


                  <div className="mt-4 flex gap-4">
                    <Button variant="primary" className="flex-1">
                      Get Started
                    </Button>
                  </div>
                </nav>
              </motion.div>
          )}
        </AnimatePresence>
      </header>
  )
}
